/**
 * @file sanitizeResponse.ts
 * @description Utility for sanitizing API responses to filter out sensitive information
 * This utility recursively traverses response objects and replaces sensitive values with
 * a redacted placeholder. It identifies sensitive keys through pattern matching.
 *
 * Part of the security remediation for sensitive data exposure (2025-04-30)
 */

// Whitelist of keys that should NOT be redacted even if they match sensitive patterns
// These are business logic fields that happen to contain sensitive-looking substrings
const WHITELISTED_KEYS = [
  "eligibleForOnePass", // Business logic field - contains "pass" but is not a password
  "onePassEnabled",     // Feature flag - contains "pass" but is not a password
  "onePassUserId",      // User identifier - contains "pass" but is not a password
];

// List of patterns that indicate sensitive information
// These patterns are used to identify keys that might contain credentials, tokens, etc.
const SENSITIVE_KEY_PATTERNS = [
  "token",
  "api_token",
  "apitoken",
  "auth_token",
  "authtoken",
  "key",
  "apikey",
  "api_key",
  "access_key",
  "secret_key",
  "password",
  "passwd",
  "pass",
  "secret",
  "credential",
  "private",
  "authorization",
  "bearer",
  "basic_auth",
  "signin",
  "login",
  "certificate",
  "access",
  "refresh",
  "verificationToken",
  "verificationId",
];

// Default placeholder for redacted information
const REDACTED_VALUE = "[REDACTED]";

/**
 * Determines if a key is sensitive based on the defined patterns
 *
 * @param key - The key to check
 * @returns boolean - True if the key matches a sensitive pattern
 */
const isSensitiveKey = (key: string): boolean => {
  // Check whitelist first - these keys should never be redacted
  if (WHITELISTED_KEYS.some((whitelisted) => key === whitelisted)) {
    return false;
  }

  // Convert key to lowercase for case-insensitive matching
  const lowerKey = key.toLowerCase();

  // Check if the key contains any of the sensitive patterns
  return SENSITIVE_KEY_PATTERNS.some((pattern) => lowerKey.includes(pattern));
};

/**
 * Recursively sanitizes an object by replacing sensitive values
 * Handles nested objects, arrays, and primitive values
 *
 * @param data - The data to sanitize (any type)
 * @returns The sanitized data with sensitive values redacted
 */
const sanitizeRecursive = <T>(data: T): T => {
  // Handle null or undefined
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitive types (string, number, boolean)
  if (typeof data !== "object") {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeRecursive(item)) as unknown as T;
  }

  // Handle objects
  const sanitized = { ...data };

  for (const key in sanitized) {
    if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
      // If the key is sensitive, redact its value
      if (isSensitiveKey(key)) {
        // @ts-ignore - We know this property exists
        sanitized[key] = REDACTED_VALUE;
      }
      // Otherwise, recursively sanitize its value if it's an object or array
      else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
        // @ts-ignore - We know this property exists
        sanitized[key] = sanitizeRecursive(sanitized[key]);
      }
    }
  }

  return sanitized;
};

/**
 * Sanitizes an API response to remove sensitive information
 * This function serves as the main entry point for the sanitization process
 *
 * @param response - The API response to sanitize
 * @param debug - Whether to log debug information
 * @returns The sanitized response with sensitive values redacted
 */
export const sanitizeResponse = <T>(response: T, debug = false): T => {
  if (debug) {
    // Safely log the structure of the response without revealing sensitive values
    console.log(
      "[Sanitizer] Processing response type:",
      typeof response === "object"
        ? Array.isArray(response)
          ? "array"
          : "object"
        : typeof response
    );
  }

  // Perform the sanitization
  const sanitized = sanitizeRecursive(response);

  if (debug) {
    // Log sanitization completion
    console.log("[Sanitizer] Response sanitized successfully");
  }

  return sanitized;
};

/**
 * Sanitizes a cookie to remove sensitive information
 * Uses the same patterns as sanitizeResponse for consistency
 *
 * @param cookie - The cookie to sanitize
 * @returns The sanitized cookie with sensitive values redacted
 */
export const sanitizeCookie = <T>(cookie: T): T => {
  return sanitizeRecursive(cookie);
};

/**
 * Helper function to get a count of redacted fields
 * Useful for reporting and verification
 *
 * @param original - The original unsanitized data
 * @param sanitized - The sanitized data
 * @returns number - Count of redacted fields
 */
export const countRedactedFields = <T>(original: T, sanitized: T): number => {
  let count = 0;

  // Helper function to compare objects and count redactions
  const compare = (orig: any, clean: any, path = ""): void => {
    if (
      orig === null ||
      orig === undefined ||
      clean === null ||
      clean === undefined
    ) {
      return;
    }

    if (typeof orig !== "object" || typeof clean !== "object") {
      if (clean === REDACTED_VALUE && orig !== REDACTED_VALUE) {
        count++;
        console.log(`[Sanitizer] Redacted field at ${path}`);
      }
      return;
    }

    if (Array.isArray(orig)) {
      if (Array.isArray(clean)) {
        for (let i = 0; i < orig.length; i++) {
          compare(orig[i], clean[i], `${path}[${i}]`);
        }
      }
      return;
    }

    for (const key in orig) {
      if (Object.prototype.hasOwnProperty.call(orig, key)) {
        compare(orig[key], clean[key], path ? `${path}.${key}` : key);
      }
    }
  };

  compare(original, sanitized);
  return count;
};

/**
 * Sanitizes an API error response
 * Special handling for error objects which may have different structures
 *
 * @param error - The error to sanitize
 * @returns The sanitized error
 */
export const sanitizeError = (error: any): any => {
  if (!error) return error;

  // Create a safe copy of the error that we can modify
  const sanitizedError = {
    ...error,
    message: error.message,
  };

  // Handle common error object properties that might contain sensitive data
  if (error.config) {
    sanitizedError.config = sanitizeRecursive(error.config);
  }

  if (error.request) {
    // Sanitize request headers and other potentially sensitive parts
    sanitizedError.request = {
      ...error.request,
      // Redact any headers that might contain auth info
      headers: sanitizeRecursive(error.request.headers || {}),
    };
  }

  if (error.response) {
    // Sanitize the response data
    sanitizedError.response = {
      ...error.response,
      data: sanitizeRecursive(error.response.data || {}),
      // Redact headers except those needed for debugging
      headers: sanitizeRecursive(error.response.headers || {}),
    };
  }

  return sanitizedError;
};
